"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { COOKIE_CONSENT_CHANGE_EVENT } from "@/lib/consent/state";
import type { ErrorPageContent } from "@/lib/content/entries/error-pages";
import type { ErrorPageRecovery } from "@/lib/error-pages/recovery-routes";
import { pageNotFoundCaptureCoordinator } from "@/lib/error-pages/page-not-found-capture";
import { trackPostHog } from "@/lib/posthog/events";

type ErrorPageRecoveryProps = {
  readonly content: ErrorPageContent;
  readonly recovery: ErrorPageRecovery;
};

export default function ErrorPageRecovery({ content, recovery }: ErrorPageRecoveryProps) {
  const [query, setQuery] = useState("");
  const normalizedQuery = normalizeSearchValue(query);
  const matches = useMemo(() => {
    if (!normalizedQuery) return [];

    const terms = normalizedQuery.split(/\s+/).filter(Boolean);

    return recovery.searchRoutes
      .filter((route) => {
        const haystack = normalizeSearchValue(`${route.label} ${route.description} ${route.href} ${route.scope}`);
        return terms.every((term) => haystack.includes(term));
      })
      .slice(0, 6);
  }, [normalizedQuery, recovery.searchRoutes]);

  useEffect(
    () =>
      pageNotFoundCaptureCoordinator.start({
        consentEventName: COOKIE_CONSENT_CHANGE_EVENT,
        consentTarget: window,
        getPostHogTracker: () =>
          typeof window.__seohPostHogTrackEvent === "function"
            ? (payload) => trackPostHog("page_not_found", payload)
            : undefined,
        hasAnalyticsConsent: () => window.__seohConsent?.has("analytics") === true,
        locale: recovery.locale,
        origin: window.location.origin,
        pathname: window.location.pathname,
        referrer: document.referrer,
        userAgent: window.navigator.userAgent,
      }),
    [recovery.locale],
  );

  return (
    <section className="redesign-section redesign-section--muted" aria-labelledby="error-page-suggestions">
      <div className="container">
        <div className="marketing-section-heading">
          <p className="marketing-eyebrow">{content.suggestionsEyebrow}</p>
          <h2 id="error-page-suggestions">{content.suggestionsTitle}</h2>
        </div>

        <div role="search" aria-label={content.suggestionsTitle} data-error-page-search>
          <label className="visually-hidden" htmlFor="error-page-search-input">
            {content.suggestionsTitle}
          </label>
          <input
            id="error-page-search-input"
            className="form-control"
            type="search"
            autoComplete="off"
            value={query}
            placeholder={content.suggestionsTitle}
            onChange={(event) => setQuery(event.currentTarget.value)}
          />
          {matches.length > 0 ? (
            <div className="redesign-proof-grid" data-error-page-search-results>
              {matches.map((route) => (
                <Link className="redesign-proof-card" href={route.href} key={route.href}>
                  <span>{route.label}</span>
                  <p>{route.description}</p>
                </Link>
              ))}
            </div>
          ) : null}
        </div>

        <div className="redesign-proof-grid" data-error-page-recovery-links>
          {recovery.suggestions.map((suggestion) => (
            <Link className="redesign-proof-card" href={suggestion.href} key={suggestion.href}>
              <span>{suggestion.label}</span>
              <p>{suggestion.description}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function normalizeSearchValue(value: string): string {
  return value.normalize("NFKC").toLocaleLowerCase().trim();
}
